<script setup lang="ts">
import type { LevelBenefitTag } from '@/utils/level-benefits';

// Chip de benefício da faixa de nível (Figma 22631:60025/60029/60033).
// Componente BURRO: recebe o benefício pronto do catálogo
// (`utils/level-benefits.ts`) e não sabe de onde ele veio — quem responde
// "este jogador tem o benefício?" é o `useLevelBenefits`, lendo as
// `core_public_tags`.
//
// Não há arte aqui. O que o Figma exporta como "ícone" do chip é só uma lasca
// arredondada com halo colorido — uma forma, não um desenho —, então ela sai em
// CSS e nenhum SVG precisa ser servido por chip.
type Props = {
  benefit: LevelBenefitTag;
};

defineProps<Props>();
</script>

<template>
  <span
    class="chip"
    :style="{ borderColor: benefit.accent }"
  >
    <span
      class="chip__cap"
      :style="{
        background: benefit.capBackground,
        color: benefit.capInk,
        boxShadow: `0 0 9.13px ${benefit.capGlow}`,
      }"
    >
      <!-- O "+" é DESENHO, não letra. Como glifo ele era um caso perdido: fica
           no eixo matemático da fonte (mais baixo que a caixa alta do "2X"),
           precisa de um corpo maior que os outros pra ter o mesmo tamanho
           óptico, e qualquer acerto em px se desalinhava de novo a cada zoom,
           porque quem arredonda ali é o rasterizador da fonte. Em SVG a cruz é
           geometria: centrada no próprio `viewBox`, escala com o zoom sem
           depender de métrica nenhuma. As barras são inclinadas junto com o
           itálico dos rótulos. -->
      <svg
        v-if="benefit.prefix === '+'"
        aria-hidden="true"
        class="chip__plus"
        fill="none"
        viewBox="0 0 12 12"
      >
        <path
          d="M6 2.4v7.2M2.4 6h7.2"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-width="2.2"
        />
      </svg>

      <template v-else>{{ benefit.prefix }}</template>
    </span>

    <span class="chip__label">{{ benefit.label }}</span>
  </span>
</template>

<style scoped lang="scss">
// Toda medida do chip é a do Figma multiplicada por `--chip-scale`, que quem
// monta define no CONTAINER (o mobile fica no 1; a placa do desktop pede 1,5).
// É o que permite um componente só pros dois tamanhos: com `transform: scale`
// a pílula não empurraria os vizinhos, e com um segundo arquivo seriam duas
// tabelas de medida pra manter em sincronia.
// A escala NÃO entra no `box-shadow` interno nem no arredondamento (999px já é
// "cápsula" em qualquer tamanho): a sombra é o que afunda a pílula na placa, e
// ampliá-la 1,5× só a deixaria pesada.
@function s($value) {
  @return calc(var(--chip-scale, 1) * #{$value});
}

// Pílula de 12,693 de altura com traço de 0,503 na cor do benefício e o mesmo
// fundo azul dos três (é do chip, não do elo). A sombra é INTERNA: afunda a
// pílula na placa em vez de levantá-la.
.chip {
  @apply inline-flex shrink-0 items-center overflow-hidden;

  height: s(12.693px);
  padding-right: s(3px);
  gap: s(1px);
  border-width: 0.503px;
  border-style: solid;
  border-radius: 999px;
  background: rgba(0, 64, 255, 0.3);
  box-shadow: inset 0 1.141px 2.282px rgba(0, 0, 0, 0.5);
}

// A lasca colorida encosta na quina esquerda e é arredondada só desse lado. A
// largura é do CONTEÚDO, não fixa: "2X" e "+" têm larguras diferentes no
// desenho (16,4 e 11,1) e é exatamente a diferença dos dois glifos.
// O desenho inclina a borda direita da lasca em ~1,4px pra acompanhar o itálico
// do texto; fica em ~6°, invisível nesta altura, e reproduzir isso custaria o
// arredondamento da esquerda (clip-path não convive com border-radius).
.chip__cap {
  @apply flex h-full shrink-0 items-center justify-center;

  padding: 0 s(3px) 0 s(2.3px);
  border-radius: 999px s(2px) s(2px) 999px;
  // A cor do texto vem do benefício (`capInk`), não daqui: ela é escolhida pelo
  // contraste contra a lasca, e lasca clara não aceita tinta branca.
  font-size: s(8.446px);
  font-style: italic;
  font-weight: 800;
  line-height: 1;
}

// A cruz: 7,6 de lado — a altura óptica do "2X" ao lado, e não a da caixa de
// texto dele. `block` pra não ganhar a linha de base do texto (que traria de
// volta justo o desalinhamento que o SVG veio resolver), e a inclinação é a
// mesma dos rótulos itálicos.
.chip__plus {
  @apply block shrink-0;

  width: s(7.6px);
  height: s(7.6px);
  transform: skewX(-12deg);
}

.chip__label {
  @apply shrink-0 whitespace-nowrap;

  color: #fff;
  // 7 e não os 6,033 do desenho: o rótulo tem contraste de sobra (13,6:1), o
  // que faltava era TAMANHO — 6px num celular de verdade vira textura. O chip
  // acompanha porque a largura é de conteúdo.
  font-size: s(7px);
  font-style: italic;
  font-weight: 700;
  line-height: 1;
}
</style>
